import { Form } from "formik";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as yup from "yup";

import { addToast, toastMessageTypes } from "@/redux/actions/toaster";
import { Button, ButtonSizes, ButtonTypes, CustomForm, Modal } from "@/component-library";
import { carePlanRequests } from "@/api/(requests)/carePlan";
import { CharacterCounter } from "@/component-library/CharacterCounter/CharacterCounter";
import { CloseIcon } from "@/components/Icons/CloseIcon";
import { FormRadioGroup } from "@/component-library/FormFields/FormRadioGroup/FormRadioGroup";
import { FormSelect } from "@/component-library/FormFields/FormSelect/FormSelect";
import { FormTextInput } from "@/component-library/FormFields/FormTextInput/FormTextInput";
import { hideLoader, showLoader } from "@/redux/actions/loader";
import { ICON_SIZES, ICON_TYPES } from "@/constants/icons";

import "@/component-library/Link/styles.scss";
import "./CareTeamModalityModal.styles.scss";

const OTHER_TEXT = "Other (Specify...)";

const MODAL_MODES = {
  ADD: "add",
  EMPTY: "empty",
  UPDATE: "update",
};

const MODALITY_ALLOWED_ROLES = {
  BHS: "Behavioral Specialist",
  CASE: "Case Manager",
  PEER: "Peer",
  PRESCRIBER: "Prescriber",
};

const DEFAULT_APPOINTMENT_ROLES = [MODALITY_ALLOWED_ROLES.BHS, MODALITY_ALLOWED_ROLES.PRESCRIBER];

const MODALITY_FORM_VALIDATION = yup.object().shape({
  default_appointment_type: yup.string().when("provider_type", {
    is: (value) => DEFAULT_APPOINTMENT_ROLES.includes(value),
    then: yup.string().required("Appointment duration is required"),
  }),
  frequency: yup.string().required("Modality frequency is required"),
  modality: yup.string().when("provider_type", {
    is: MODALITY_ALLOWED_ROLES.BHS,
    then: yup.string().required("Modality is required"),
  }),
  modality_other: yup.string().when("modality", {
    is: OTHER_TEXT,
    then: yup
      .string()
      .matches(/^[a-zA-Z0-9, ']+$/, "Special characters are not allowed")
      .required(`Modality specification is required when selecting "${OTHER_TEXT}"`),
  }),
});

const formatOptions = (optionsList) => {
  const res = [];

  optionsList.forEach((option) => {
    res.push({ label: option, value: option });
  });

  return res;
};

const formatDefaultAppointmentOptions = (optionsObject) => {
  const res = [];

  Object.keys(optionsObject).forEach((key) => {
    res.push({ label: optionsObject[key].text, value: key });
  });

  return res;
};

export const CareTeamModalityModal = (props) => {
  const { careTeamMember, mode, options, patientId } = props;
  const dispatch = useDispatch();

  const isBHS = careTeamMember?.type === MODALITY_ALLOWED_ROLES.BHS;
  const titleOperation = mode === MODAL_MODES.ADD ? "Select" : "Edit";
  const buttonOperation = mode === MODAL_MODES.ADD ? "Add" : "Save";
  const bhsTitle = `
        ${titleOperation}
        ${mode === MODAL_MODES.ADD ? "a " : ""}
        primary treatment modality`;
  const regularTitle = `${titleOperation} modality details`;
  const title = isBHS ? bhsTitle : regularTitle;
  const defaultModalityValue = isBHS ? undefined : options.modality[careTeamMember?.type][0];

  const renderForm = (formikProps) => {
    const [showModalityOther, setShowModalityOther] = useState(
      careTeamMember?.modality === OTHER_TEXT,
    );

    useEffect(() => {
      if (formikProps.values.modality === OTHER_TEXT) {
        setShowModalityOther(true);
      } else {
        setShowModalityOther(false);
        formikProps.setFieldValue("modality_other", "");
      }
    }, [formikProps.values.modality]);

    return (
      <Form>
        <div className="CareTeamModalityModal--modal_form">
          {isBHS && (
            <FormRadioGroup
              className="AdvancedCareActivityForm-form_field"
              formikProps={formikProps}
              id="CareTeamModalityModal_ModalityInput"
              name="modality"
              options={formatOptions(options.modality[MODALITY_ALLOWED_ROLES.BHS])}
              title="Modality"
              triggerErrorOnChange
            />
          )}
          {isBHS && showModalityOther && (
            <div className="CareTeamModalityModal--other_input_container">
              <FormTextInput
                className="AdvancedCareActivityForm-form_field"
                formikProps={formikProps}
                id="CareTeamModalityModal_ModalityOtherInput"
                maxLength={250}
                name="modality_other"
                title="Specify Modality"
                triggerErrorOnChange
              />
              <CharacterCounter
                characterLimit={250}
                className="CareTeamModalityModal--input_character_counter"
                length={formikProps.values?.modality_other?.length}
              />
            </div>
          )}
          {!isBHS && (
            <div className="FormField-header_container" data-public>
              <h3 className="FormField-title">Modality</h3>
              <p className="text-break">{formikProps.values.modality}</p>
            </div>
          )}
          <FormSelect
            className="AdvancedCareActivityForm-form_field CareTeamModalityModal--select"
            formikProps={formikProps}
            id="CareTeamModalityModal_FrequencyInput"
            name="frequency"
            options={formatOptions(options.frequency)}
            placeholder="Select"
            subTitle="How often you plan to meet with the patient"
            title="Modality Frequency"
            triggerErrorOnChange
          />
          {DEFAULT_APPOINTMENT_ROLES.includes(careTeamMember?.type) && (
            <FormSelect
              className="AdvancedCareActivityForm-form_field CareTeamModalityModal--select"
              formikProps={formikProps}
              id="CareTeamModalityModal_DefaultAppointmentTypeInput"
              name="default_appointment_type"
              options={formatDefaultAppointmentOptions(
                options.default_appointment_type[careTeamMember?.type],
              )}
              placeholder="Select"
              subTitle="Sets this patient's self-scheduling duration without affecting options for you and other providers"
              title="Appointment Duration"
              triggerErrorOnChange
            />
          )}
          <div className="AdvancedCareActivityForm-button_wrapper">
            <Button
              buttonSize={ButtonSizes.medium}
              buttonType={ButtonTypes.primaryV2}
              id="CareTeamModalityModal_SubmitButton"
              type="submit"
            >
              {buttonOperation}
            </Button>
            <Button
              buttonSize={ButtonSizes.medium}
              buttonType={ButtonTypes.primaryOutlineV2}
              id="CareTeamModalityModal_CancelButton"
              onClick={() => {
                formikProps.resetForm();
                props.onClose();
              }}
              type="button"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Form>
    );
  };

  return (
    <Modal
      cardClassName="CareTeamModalityModal--dialog"
      className="CareTeamModalityModal--modal"
      show={true}
    >
      <CloseIcon
        className="CareTeamModalityModal--modal_close_icon"
        iconSize={ICON_SIZES.SMALL}
        iconType={ICON_TYPES.OUTLINE}
        onClick={props.onClose}
      />
      <div className="CareTeamModalityModal--modal_internal">
        <div className="CareTeamModalityModal--modal_header">
          <h1 className="CareTeamModalityModal--modal_title">{title}</h1>
        </div>

        {careTeamMember && (
          <CustomForm
            initialValues={{
              default_appointment_type: careTeamMember?.default_appointment_type,
              frequency: careTeamMember?.frequency,
              modality: careTeamMember?.modality || defaultModalityValue,
              modality_other: careTeamMember?.modality_other,
              provider_type: careTeamMember.type,
            }}
            onSubmit={async (formData) => {
              const data = {
                default_appointment_type: formData.default_appointment_type,
                frequency: formData.frequency,
                modality: formData.modality,
                modality_other: formData?.modality_other,
                provider_id: careTeamMember.provider_id,
              };

              dispatch(showLoader());
              await carePlanRequests.change_modality({
                data,
                urlParams: { patientId: patientId },
              });
              await props.onModalSubmission();
              dispatch(hideLoader());
              if (mode === MODAL_MODES.UPDATE) {
                dispatch(
                  addToast({
                    dismissTimeout: 1000,
                    message: "Updated",
                    messageType: toastMessageTypes.success_v2,
                  }),
                );
              }
            }}
            render={renderForm}
            validateOnBlur={true}
            validateOnChange={true}
            validationSchema={MODALITY_FORM_VALIDATION}
          />
        )}
      </div>
    </Modal>
  );
};
